// AcceptingSubAgentsToggleCard — Phase R7 follow-up (2026-05-13).
//
// Full descriptive card (icon + uppercase header + two-state body
// copy + pill toggle) mirroring the players-side
// AcceptingPlayersToggleCard. Replaces the earlier compact pill
// (which sat in the SubagentsListPanel header) — that version
// carried no body copy and made it impossible for an agent to
// understand what the toggle did without hovering for a tooltip.
//
// Behaviour:
//   * Reads `admin.is_accepting_agents` from useAdminAuth.
//   * Flips via PATCH /admin/me-agent/opt-out-agents { accepting }.
//   * Optimistic UI: instant flip, rolls back on error.
//   * Calls refresh() on success so the auth context re-hydrates.
//
// Default export name is `AcceptingAgentsToggle` (unchanged) so the
// existing import in SubagentsListPanel.tsx keeps working.

import { useEffect, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { useDownlineLabel } from '@baaligames/shared-ui/admin/lib/downlineLabel';
import { ApiError } from '@baaligames/shared-ui/lib/api';

export default function AcceptingAgentsToggle() {
  const { admin, refresh } = useAdminAuth();
  const dl = useDownlineLabel();   // "Master(s)" for a Super Master, "Agent(s)" for a Master
  const me = admin as any;
  const initial = !!me?.is_accepting_agents;
  const [accepting, setAccepting] = useState<boolean>(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { setAccepting(!!me?.is_accepting_agents); }, [me?.is_accepting_agents]);

  if (admin?.role !== 'agent') return null;

  async function flip() {
    if (busy) return;
    const next = !accepting;
    setAccepting(next);          // optimistic
    setBusy(true); setErr(null);
    try {
      await adminApi.patch('/admin/me-agent/opt-out-agents', { accepting: next });
      await refresh();
    } catch (e) {
      setAccepting(!next);       // rollback
      setErr((e as ApiError).message || 'Could not update');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`rounded-xl border-2 p-4 ${accepting
      ? 'border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/20'
      : 'border-amber-400 dark:border-amber-500/50 bg-amber-50 dark:bg-amber-950/25'}`}>
      <div className="flex items-start gap-3">
        <span className={`grid place-items-center w-9 h-9 rounded-xl shrink-0 ${accepting
          ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
          : 'bg-amber-500/20 text-amber-700 dark:text-amber-300'}`}>
          <Icon name={accepting ? 'task_alt' : 'pause_circle'} size={20} filled />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-extrabold uppercase tracking-wider mb-0.5
                          text-arena-700 dark:text-neon-200/85">
            Receiving new {dl.lowerPlural}
          </div>
          <div className={`text-[13px] leading-relaxed ${accepting
            ? 'text-emerald-900 dark:text-emerald-100'
            : 'text-amber-900 dark:text-amber-100'}`}>
            {accepting
              ? <>You're <strong>ACCEPTING</strong> new {dl.lowerPlural}. Admin can move new {dl.lowerPlural} into your downline via the bulk-reassign tool. Direct creations through the <strong>+ New {dl.singular}</strong> button below are unaffected by this toggle.</>
              : <>You've <strong>OPTED OUT</strong>. Admin's bulk-reassign tool will skip you — only {dl.lowerPlural} you create directly via the <strong>+ New {dl.singular}</strong> button below will appear here. Your existing {dl.lowerPlural} stay where they are.</>}
          </div>
          {err && <p className="text-[11.5px] text-rose-600 dark:text-rose-300 mt-1.5">{err}</p>}
        </div>
        <button
          type="button"
          onClick={flip}
          disabled={busy}
          aria-pressed={accepting}
          aria-label={accepting ? `Stop accepting new ${dl.lowerPlural}` : `Start accepting new ${dl.lowerPlural}`}
          className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full
                      transition disabled:opacity-50 ${accepting
                        ? 'bg-emerald-600'
                        : 'bg-amber-500'}`}>
          <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
            accepting ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>
    </div>
  );
}
